import React, { useCallback, useMemo } from "react"

import { Button, Tooltip } from "@coval/ui"
import { Info, Plus } from "lucide-react"
import { useTheme } from "next-themes"

import type {
  TraceFilterConfig,
  TraceFilterEntryConfig,
  TraceFilterJoin,
} from "@/types/metrics/metadata"

import {
  DraggableFilterList,
  JoinToggle,
  useFilterListSection,
} from "../../filter-list"
import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../../styles"
import {
  createDefaultTraceFilterEntry,
  MAX_TRACE_FILTERS_PER_CATEGORY,
  type TraceFilterPreset,
} from "../types"
import { TraceFilterRow } from "./TraceFilterRow"

interface TraceFilterSectionProps {
  value: TraceFilterConfig | undefined
  onChange: (value: TraceFilterConfig | undefined) => void
}

function useTraceFilter(
  value: TraceFilterConfig | undefined,
  onChange: (value: TraceFilterConfig | undefined) => void,
) {
  const activePreset: TraceFilterPreset = useMemo(
    () => (value === undefined ? "full" : "custom"),
    [value],
  )

  const handlePresetChange = useCallback(
    (preset: TraceFilterPreset) => {
      if (preset === "full") {
        onChange(undefined)
      } else if (preset === "custom" && activePreset !== "custom") {
        onChange({})
      }
    },
    [activePreset, onChange],
  )

  const handleConfigChange = useCallback(
    (next: TraceFilterConfig) => {
      onChange(next)
    },
    [onChange],
  )

  return { activePreset, handlePresetChange, handleConfigChange }
}

const TraceFilterToggle: React.FC<TraceFilterSectionProps> = ({
  value,
  onChange,
}) => {
  const { theme } = useTheme()
  const { activePreset, handlePresetChange } = useTraceFilter(value, onChange)

  const selectedStyles = `${theme === "dark" ? "bg-content4" : "bg-white"} text-foreground shadow-sm`
  const unselectedStyles = "text-coval-text-muted hover:text-coval-text"

  const presets: { key: TraceFilterPreset; label: string }[] = [
    { key: "full", label: "All spans" },
    { key: "custom", label: "Custom" },
  ]

  return (
    <div className={`${FIELD_CONTAINER_STYLES} flex-1`}>
      <div className={LABEL_ROW_STYLES}>
        <label className={LABEL_STYLES}>Trace Scope</label>
        <Tooltip
          content={
            <div className="max-w-xs p-1">
              <p className="text-sm">
                Choose which trace spans contribute to the metric. Filter by
                span name, span attribute, time range, span turn position, or
                OTel status.
              </p>
            </div>
          }
          side="top"
        >
          <Info className={INFO_ICON_STYLES} />
        </Tooltip>
      </div>

      <div className="flex h-control rounded-md bg-surface-1 p-1">
        {presets.map((preset) => (
          <button
            key={preset.key}
            type="button"
            aria-label={`Trace scope ${preset.label}`}
            aria-pressed={activePreset === preset.key}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              handlePresetChange(preset.key)
            }}
            className={`flex-1 px-3 text-sm font-medium rounded-sm transition-all cursor-pointer ${
              activePreset === preset.key ? selectedStyles : unselectedStyles
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  )
}

const TraceFilterDetails: React.FC<TraceFilterSectionProps> = ({
  value,
  onChange,
}) => {
  const { activePreset, handleConfigChange } = useTraceFilter(value, onChange)

  if (activePreset !== "custom" || !value) {
    return null
  }

  return (
    <div className="w-full">
      <TraceFilterLists config={value} onChange={handleConfigChange} />
    </div>
  )
}

export const TraceFilterSection: React.FC<TraceFilterSectionProps> = ({
  value,
  onChange,
}) => {
  return (
    <div className={FIELD_CONTAINER_STYLES}>
      <TraceFilterToggle value={value} onChange={onChange} />
      <TraceFilterDetails value={value} onChange={onChange} />
    </div>
  )
}

interface TraceFilterListsProps {
  config: TraceFilterConfig
  onChange: (config: TraceFilterConfig) => void
}

const TraceFilterLists: React.FC<TraceFilterListsProps> = ({
  config,
  onChange,
}) => {
  const includeFilters = config.include ?? []
  const excludeFilters = config.exclude ?? []
  const includeJoins = config.include_joins ?? []
  const excludeJoins = config.exclude_joins ?? []

  const includeSection = useFilterListSection<TraceFilterEntryConfig>({
    filters: includeFilters,
    joins: includeJoins,
    max: MAX_TRACE_FILTERS_PER_CATEGORY,
    createDefault: () => createDefaultTraceFilterEntry("span_name"),
    onChange: (nextFilters, nextJoins) =>
      onChange({
        ...config,
        include: nextFilters,
        include_joins: nextJoins as TraceFilterJoin[] | undefined,
      }),
  })

  const excludeSection = useFilterListSection<TraceFilterEntryConfig>({
    filters: excludeFilters,
    joins: excludeJoins,
    max: MAX_TRACE_FILTERS_PER_CATEGORY,
    createDefault: () => createDefaultTraceFilterEntry("status"),
    onChange: (nextFilters, nextJoins) =>
      onChange({
        ...config,
        exclude: nextFilters,
        exclude_joins: nextJoins as TraceFilterJoin[] | undefined,
      }),
  })

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className={LABEL_STYLES}>Include</label>
        </div>
        {includeFilters.length === 0 ? (
          <Button
            variant="ghost"
            onClick={includeSection.handleAdd}
            aria-label="Add include trace filter"
            className="text-coval-text-muted"
          >
            <Plus />
            Add include filter
          </Button>
        ) : (
          <div className="space-y-1">
            <DraggableFilterList<TraceFilterEntryConfig>
              filters={includeFilters}
              onChange={includeSection.handleUpdate}
              onRemove={includeSection.handleRemove}
              onReorder={includeSection.handleReorder}
              renderRow={({ filter, onChange: rowOnChange, onRemove }) => (
                <TraceFilterRow
                  filter={filter}
                  onChange={rowOnChange}
                  onRemove={onRemove}
                />
              )}
              renderConnector={(index) => (
                <JoinToggle
                  value={includeJoins[index] ?? "or"}
                  onChange={(next) =>
                    includeSection.handleJoinChange(index, next)
                  }
                />
              )}
              getKey={(filter, index) => filter.id ?? `include-${index}`}
              ariaPrefix="include"
            />
            {includeSection.belowCap && (
              <Button
                variant="ghost"
                onClick={includeSection.handleAdd}
                aria-label="Add another include trace filter"
                className="text-coval-text-muted h-7 text-xs mt-2"
              >
                <Plus />
                Add another
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className={LABEL_STYLES}>Exclude</label>
        </div>
        {excludeFilters.length === 0 ? (
          <Button
            variant="ghost"
            onClick={excludeSection.handleAdd}
            aria-label="Add exclude trace filter"
            className="text-coval-text-muted"
          >
            <Plus />
            Add exclude filter
          </Button>
        ) : (
          <div className="space-y-1">
            <DraggableFilterList<TraceFilterEntryConfig>
              filters={excludeFilters}
              onChange={excludeSection.handleUpdate}
              onRemove={excludeSection.handleRemove}
              onReorder={excludeSection.handleReorder}
              renderRow={({ filter, onChange: rowOnChange, onRemove }) => (
                <TraceFilterRow
                  filter={filter}
                  onChange={rowOnChange}
                  onRemove={onRemove}
                />
              )}
              renderConnector={(index) => (
                <JoinToggle
                  value={excludeJoins[index] ?? "or"}
                  onChange={(next) =>
                    excludeSection.handleJoinChange(index, next)
                  }
                />
              )}
              getKey={(filter, index) => filter.id ?? `exclude-${index}`}
              ariaPrefix="exclude"
            />
            {excludeSection.belowCap && (
              <Button
                variant="ghost"
                onClick={excludeSection.handleAdd}
                aria-label="Add another exclude trace filter"
                className="text-coval-text-muted h-7 text-xs mt-2"
              >
                <Plus />
                Add another
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
